import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { CurrencyService } from 'src/app/modules/finance/services/currency.service';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  TableAction,
  TableColumn,
} from 'src/app/shared/models/table-config.model';
import { TableLazyLoadEvent } from 'primeng/table';
import { CurrencyAdminModalComponent } from './currency-admin-modal/currency-admin-modal.component';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';
import { AuditHistoryModalComponent } from 'src/app/shared/components/audit-history-modal/audit-history-modal.component';

@Component({
  selector: 'app-currency-management',
  templateUrl: './currency-management.component.html',
  styleUrls: ['./currency-management.component.scss'],
  standalone: false,
  providers: [DialogService],
})
export class CurrencyManagementComponent implements OnInit {
  allCurrencies: any[] = [];
  tableData: any[] = [];
  totalRecords: number = 0;
  loading: boolean = true;
  searchText: string = '';
  isGlobalAdmin: boolean = false;

  ref: DynamicDialogRef | undefined;

  tableColumns: TableColumn[] = [];
  tableActions: TableAction[] = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openModal(row.id),
    },
    {
      label: 'History',
      icon: 'pi pi-history',
      styleClass: 'p-button-text p-button-rounded text-gray-500 hover:bg-gray-50',
      command: (row) => this.openAuditHistory(row),
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      styleClass: 'p-button-text p-button-danger',
      command: (row) => this.deleteCurrency(row),
    },
  ];

  constructor(
    private readonly currencyService: CurrencyService,
    private readonly authService: AuthService,
    private readonly dialogService: DialogService,
    private readonly toastService: AppToastService,
  ) {}

  ngOnInit(): void {
    this.isGlobalAdmin =
      this.authService.roleMatch(['Admin']) &&
      this.authService.roleMatch(['TenantAdmin']);

    this.tableColumns = this.isGlobalAdmin
      ? [
          {
            field: 'id',
            header: 'ID',
            type: 'text',
            width: '60px',
            sortable: true,
            linkAction: (row) => this.openModal(row.id),
          },
          {
            field: 'acronym',
            header: 'ACRONYM',
            type: 'text',
            width: '80px',
            sortable: true,
          },
          {
            field: 'flag',
            header: 'FLAG',
            type: 'flag',
            width: '60px',
            sortable: false,
          },
          {
            field: 'name',
            header: 'NAME',
            type: 'text',
            width: 'auto',
            sortable: true,
          },
          {
            field: 'tenantName',
            header: 'TENANT',
            type: 'text',
            width: '30%',
            sortable: true,
          },
          { field: 'actions', header: '', type: 'actions', width: '80px' },
        ]
      : [
          {
            field: 'id',
            header: 'ID',
            type: 'text',
            width: '60px',
            sortable: true,
            linkAction: (row) => this.openModal(row.id),
          },
          {
            field: 'acronym',
            header: 'ACRONYM',
            type: 'text',
            width: '80px',
            sortable: true,
          },
          {
            field: 'flag',
            header: 'FLAG',
            type: 'flag',
            width: '60px',
            sortable: false,
          },
          {
            field: 'name',
            header: 'NAME',
            type: 'text',
            width: 'auto',
            sortable: true,
          },
          { field: 'actions', header: '', type: 'actions', width: '80px' },
        ];

    this.loadCurrencies();
  }

  loadCurrencies() {
    this.loading = true;
    const request$ = this.isGlobalAdmin
      ? this.currencyService.getAllCurrencies()
      : this.currencyService.getCurrencies();

    request$.pipe(finalize(() => (this.loading = false))).subscribe({
      next: (data: any[]) => {
        this.allCurrencies = data || [];
        this.totalRecords = this.allCurrencies.length;
        this._updateTableData();
      },
      error: () => {},
    });
  }

  handleSearch(text: string) {
    this.searchText = text?.toLowerCase() || '';
    this._updateTableData();
  }

  private _updateTableData(): void {
    const filtered = this.searchText
      ? this.allCurrencies.filter(
          (c) =>
            c.name?.toLowerCase().includes(this.searchText) ||
            String(c.id ?? '').toLowerCase().includes(this.searchText) ||
            c.acronym?.toLowerCase().includes(this.searchText) ||
            c.tenantName?.toLowerCase().includes(this.searchText),
        )
      : [...this.allCurrencies];
    this.tableData = filtered;
    this.totalRecords = filtered.length;
  }

  loadLazy(event: TableLazyLoadEvent) {
    if (!this.allCurrencies.length) return;
    this.loading = true;
    let filtered = this.searchText
      ? this.allCurrencies.filter(
          (c) =>
            c.name?.toLowerCase().includes(this.searchText) ||
            String(c.id ?? '').toLowerCase().includes(this.searchText) ||
            c.acronym?.toLowerCase().includes(this.searchText) ||
            c.tenantName?.toLowerCase().includes(this.searchText),
        )
      : [...this.allCurrencies];

    if (event.sortField) {
      filtered.sort((a, b) => {
        const strA = String(a[event.sortField as string] || '').toLowerCase();
        const strB = String(b[event.sortField as string] || '').toLowerCase();
        return (event.sortOrder || 1) * strA.localeCompare(strB);
      });
    }

    setTimeout(() => {
      this.tableData = filtered;
      this.totalRecords = filtered.length;
      this.loading = false;
    }, 100);
  }

  openModal(id?: number) {
    const row = id ? this.allCurrencies.find((c) => c.id === id) : null;
    this.ref = this.dialogService.open(CurrencyAdminModalComponent, {
      showHeader: false,
      width: '550px',
      contentStyle: { overflow: 'auto', padding: '0' },
      baseZIndex: 10000,
      data: { id, row, isGlobalAdmin: this.isGlobalAdmin },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result === 'success') this.loadCurrencies();
    });
  }

  openAuditHistory(currency: any) {
    this.dialogService.open(AuditHistoryModalComponent, {
      showHeader: false,
      width: '600px',
      contentStyle: { overflow: 'visible', padding: '0', borderRadius: '12px' },
      data: {
        entityType: 'Currency',
        entityId: currency.id,
        title: `Currency History — ${currency.name}`,
        fieldLabels: [
          { field: 'Id', label: 'ID' },
          { field: 'Name', label: 'Name' },
          { field: 'Acronym', label: 'Acronym' },
          { field: 'Flag', label: 'Flag' },
        ],
      },
    });
  }

  deleteCurrency(currency: any) {
    const ref = this.dialogService.open(ConfirmationModalComponent, {
      showHeader: false,
      width: '450px',
      contentStyle: { padding: '0', borderRadius: '8px' },
      data: {
        title: `Delete "${currency.name}"?`,
        message: 'This action cannot be undone.',
        confirmLabel: 'Delete',
      },
    });

    ref.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.loading = true;
        this.currencyService.remove(currency.id).subscribe({
          next: () => {
            this.toastService.success(
              'Deleted',
              'Currency deleted successfully.',
            );
            this.allCurrencies = this.allCurrencies.filter(
              (c) => c.id !== currency.id,
            );
            this._updateTableData();
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
      }
    });
  }
}
